import React, { FunctionComponent, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router';

import { Breadcrumb } from 'app/components/breadcrumb';
import { ScrollBackToTop } from 'app/components/scroll-back-to-top';
import { ChooseAccountPage } from 'app/pages/order/account/choose-account-page';
import { ProductDetailsPage } from 'app/pages/order/product-details/product-details-page';
import { RequestType } from 'app/pages/order/redux/model/order-request';
import { OrderStep } from 'app/pages/order/redux/model/order-step';
import { selectOrderRequests } from 'app/pages/order/redux/order-selector';
import { ChooseServiceLocationPage } from 'app/pages/order/service-location/choose-service-location-page';
import { SuccessPage } from 'app/pages/order/success/success-page';
import { SummaryPage } from 'app/pages/order/summary/summary-page';
import { OrderRequestsUtils } from 'app/pages/order/utils/order-requests-utils';
import { NavigationService } from 'app/services/navigation-service';
import { ProtectedContentWithoutCompletedRequests } from 'app/components/protected-content-without-completed-requests';
import { ProtectedOrderStepRoute } from 'app/components/protected-order-step-route';
import { useAppDispatch } from 'app/hooks/thunk-hooks';

import { HousingSelectPage } from './housing-select';
import { useShopStatusRequest } from './hooks/use-shop-status-request';
import { resetOrderStateForNewAccountActionCreator } from './redux/action-creators/reset-order-state-for-new-account-action-creator';

export const Order: FunctionComponent = () => {
    const orderRequests = useSelector(selectOrderRequests);
    const shopStatusRequest = useShopStatusRequest();
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (OrderRequestsUtils.isRequestPending(orderRequests, RequestType.SUBMIT_SHOPPING_CART)) {
            dispatch(resetOrderStateForNewAccountActionCreator());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ProtectedContentWithoutCompletedRequests requests={[shopStatusRequest]}>
            <Breadcrumb />
            <ScrollBackToTop />
            <Routes>
                <Route
                    path={NavigationService.CHOOSE_ACCOUNT}
                    element={
                        <ProtectedOrderStepRoute orderStep={OrderStep.CHOOSE_ACCOUNT}>
                            <ChooseAccountPage />
                        </ProtectedOrderStepRoute>
                    }
                />
                <Route
                    path={NavigationService.CHOOSE_SERVICE_LOCATION}
                    element={
                        <ProtectedOrderStepRoute orderStep={OrderStep.CHOOSE_SERVICE_LOCATION}>
                            <ChooseServiceLocationPage />
                        </ProtectedOrderStepRoute>
                    }
                />
                <Route
                    path={NavigationService.HOUSING_SELECT}
                    element={
                        <ProtectedOrderStepRoute orderStep={OrderStep.HOUSING_SELECT}>
                            <HousingSelectPage />
                        </ProtectedOrderStepRoute>
                    }
                />
                <Route
                    path={NavigationService.PRODUCT_DETAILS}
                    element={
                        <ProtectedOrderStepRoute orderStep={OrderStep.PRODUCTS_DETAILS}>
                            <ProductDetailsPage />
                        </ProtectedOrderStepRoute>
                    }
                />
                <Route
                    path={NavigationService.SUMMARY}
                    element={
                        <ProtectedOrderStepRoute orderStep={OrderStep.SUMMARY}>
                            <SummaryPage />
                        </ProtectedOrderStepRoute>
                    }
                />
                <Route
                    path={NavigationService.SUCCESS}
                    element={
                        <ProtectedOrderStepRoute orderStep={OrderStep.SUCCESS}>
                            <SuccessPage />
                        </ProtectedOrderStepRoute>
                    }
                />
                <Route path="*" element={<Navigate to={NavigationService.CHOOSE_ACCOUNT} replace={true} />} />
            </Routes>
        </ProtectedContentWithoutCompletedRequests>
    );
};
